/**
 * Pure helpers for the program "Portfolio" landing surface
 * (`program_location_map` + `program_property_grid` blocks).
 *
 * Kept dependency-light (only the `virtual-space-host` constant
 * module, which is itself pure) so the renderer's client bundle +
 * unit tests can both import it without dragging server code in.
 */

import { normalizeMatterportProviderState } from "./matterport-provider-state";
import { VIRTUAL_SPACE_HOST } from "./virtual-space-host";
import { resolveMatterportStatus, type MatterportStatusInput } from "./matterport-status";

/**
 * Short connective words that stay lowercase in title-cased proper
 * names UNLESS they lead the string. Covers the cases that actually
 * show up in US/UK street addresses + building names.
 */
const LOWERCASE_CONNECTORS: ReadonlySet<string> = new Set([
  "of",
  "the",
  "and",
  "at",
  "by",
  "in",
  "on",
  "for",
  "to",
  "a",
  "an",
  "vs",
  "de",
  "la",
  "le",
]);

/**
 * Conservative title-case for building names + street addresses.
 *
 * The Convene import left a handful of mis-cased names
 * ("780 Third avenue") next to correctly-cased ones
 * ("333 North Green Street"). A naive `.toLowerCase()` →
 * capitalize-each-word would BREAK the correct ones — it would
 * lowercase "NW" → "Nw", strip the ordinal in "7th" → "7Th", and
 * mangle "McKinley" → "Mckinley".
 *
 * So this only re-cases words that are *entirely lowercase letters*
 * — exactly the bug class — and leaves everything else (words
 * carrying an uppercase letter, a digit, or mixed case) untouched.
 * Known connectors stay lowercase when not in the leading position.
 *
 *   "780 Third avenue"          → "780 Third Avenue"   (avenue fixed)
 *   "333 North Green Street"    → unchanged
 *   "575 7th St NW"             → unchanged            (7th / NW kept)
 *   "1221 Avenue of the Americas" → unchanged          (of / the kept)
 *   "16 W Adams St"             → unchanged
 */
export function titleCaseProperName(raw: string | null | undefined): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/);
  return words
    .map((word, idx) => {
      // Any uppercase letter or digit ⇒ intentional casing
      // (directional "NW", ordinal "7th", name "McKinley"). Leave it.
      if (/[A-Z0-9]/.test(word)) return word;
      const lower = word.toLowerCase();
      if (idx > 0 && LOWERCASE_CONNECTORS.has(lower)) return lower;
      // Capitalize the first alpha char only — never touches a
      // leading "(" or other punctuation.
      return word.replace(/^[a-z]/, (c) => c.toUpperCase());
    })
    .join(" ");
}

/** Normalize a city key for grouping/counting — trimmed, with a
 *  stable "Other" bucket for blank/null cities so they never vanish
 *  silently from the grid. */
function cityKey(city: string | null | undefined): string {
  return (city ?? "").trim() || "Other";
}

/**
 * "City, ST" sub-line for a card, falling back gracefully:
 *   city + state → "Chicago, IL"
 *   city only    → "Chicago"
 *   state only   → "IL"
 *   neither      → null (caller omits the line)
 * City + state are title-cased / upper-cased for polish.
 *
 * When the city and region/state are the SAME word (case-insensitive),
 * the region is redundant and dropped — so a UK import whose city +
 * region are both "London" shows "London", not "London, London"
 * (the data bug the Convene portfolio surfaced), and "New York, New
 * York" collapses to the cleaner "New York".
 */
export function formatCityState(
  city: string | null | undefined,
  state: string | null | undefined,
): string | null {
  const c = (city ?? "").trim();
  const s = (state ?? "").trim();
  const cityPretty = c ? titleCaseProperName(c) : "";
  // 2-letter US state codes upper-case; longer names title-case.
  const statePretty = s.length === 2 ? s.toUpperCase() : titleCaseProperName(s);
  if (cityPretty && statePretty) {
    // Redundant region (city === state) → show the city once.
    if (c.toLowerCase() === s.toLowerCase()) return cityPretty;
    return `${cityPretty}, ${statePretty}`;
  }
  if (cityPretty) return cityPretty;
  if (statePretty) return statePretty;
  return null;
}

/**
 * How many entries are archived. Drives the "Show archived (N)" /
 * "Hide archived" toggle label + whether the toggle renders at all.
 */
export function countArchived<T extends { isArchived: boolean }>(
  locations: readonly T[],
): number {
  return locations.reduce((n, l) => n + (l.isArchived ? 1 : 0), 0);
}

/**
 * Filter a location set by archived visibility. When `showArchived`
 * is false (the default), archived buildings are dropped; otherwise
 * everything is returned.
 *
 * The grid derives its city chips, group-by-city sections, AND the
 * map fly-to focus id set from the RESULT of this filter — so hiding
 * archived buildings drops them from the counts, the sections (an
 * all-archived city disappears entirely), and the focus set together,
 * with no special-casing downstream.
 */
export function filterByArchivedVisibility<T extends { isArchived: boolean }>(
  locations: readonly T[],
  showArchived: boolean,
): T[] {
  return showArchived
    ? locations.slice()
    : locations.filter((l) => !l.isArchived);
}

/**
 * Distinct cities present in a location set, each with a count, for
 * the filter chip row. Sorted alphabetically with the "Other" bucket
 * sunk to the end.
 */
export function deriveCityFilters<T extends { city: string | null }>(
  locations: readonly T[],
): Array<{ city: string; count: number }> {
  const counts = new Map<string, number>();
  for (const loc of locations) {
    const key = cityKey(loc.city);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([city, count]) => ({ city, count }))
    .sort(sortCityKeyed);
}

/**
 * Group locations into city sections (each city's locations sorted by
 * name). Cities sorted alphabetically; "Other" sinks to the bottom.
 */
export function groupLocationsByCity<
  T extends { city: string | null; name: string | null },
>(locations: readonly T[]): Array<{ city: string; locations: T[] }> {
  const byCity = new Map<string, T[]>();
  for (const loc of locations) {
    const key = cityKey(loc.city);
    const arr = byCity.get(key) ?? [];
    arr.push(loc);
    byCity.set(key, arr);
  }
  return Array.from(byCity.entries())
    .map(([city, locs]) => ({
      city,
      locations: locs
        .slice()
        .sort((a, b) =>
          (a.name ?? "").localeCompare(b.name ?? "", undefined, {
            numeric: true,
          }),
        ),
    }))
    .sort((a, b) => sortCityKeyed(a, b));
}

function sortCityKeyed(a: { city: string }, b: { city: string }): number {
  if (a.city === "Other" && b.city !== "Other") return 1;
  if (b.city === "Other" && a.city !== "Other") return -1;
  return a.city.localeCompare(b.city);
}

/**
 * The iframe snippet an operator/customer pastes to embed one
 * building's Launch tour. Mirrors the snippet `share-dialog.tsx`
 * builds for embed-mode links so the two stay consistent — the
 * `/embed/<token>` route 308-redirects to the canonical tier path
 * with iframe-friendly headers.
 */
export function buildLaunchEmbedSnippet(token: string): string {
  const url = `${VIRTUAL_SPACE_HOST}/embed/${token}`;
  return `<iframe src="${url}" width="100%" height="600" frameborder="0" allow="xr-spatial-tracking; vr; gyroscope; accelerometer; fullscreen" loading="lazy"></iframe>`;
}

/**
 * Deterministic hue (0..359) from a string. Used to give each
 * building card a distinct subtle accent so a thumbnail-less grid
 * doesn't read as 37 identical rows. Stable across renders — the
 * same building always gets the same accent.
 */
export function hueFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) % 360;
  }
  return h;
}

/**
 * Whether a Matterport model `state` reads as archived/inactive —
 * the source of truth for a building's Active/Archived card status.
 *
 * Two shapes flow into `matterport_details.provider_state`:
 *   • the Convene import seeded human-readable Matterport statuses
 *     ("Archived", "Shared - Managed"), and
 *   • a live API sync (`matterport-row-mapper`) stamps the GraphQL
 *     ModelState enum ("active" / "archived" / "inactive").
 * Match the known archived/inactive vocabulary without substring inference.
 * Null / unknown / "Shared - Managed" / "Shared -
 * Transferred" / "active" are not known to be archived. That boolean
 * does not verify current tour access or account custody.
 */
export function isArchivedMatterportState(
  state: string | null | undefined,
): boolean {
  const normalized = normalizeMatterportProviderState(state);
  return normalized === "archived" || normalized === "inactive";
}

/** Current tour availability for portfolio copy, independent of account custody. */
export function matterportPortfolioTourStatus(input: Pick<MatterportStatusInput,
  "deliverableId" | "modelId" | "organizationId" | "custody" | "providerSyncSource" | "providerSyncError" | "providerState" | "providerVisibility" | "providerSyncedAt" | "now"
>): "available" | "archived" | "unknown" {
  const tour = resolveMatterportStatus(input).tour.status;
  return tour === "link" ? "available" : tour === "archived" || tour === "inactive" ? "archived" : "unknown";
}

/** First alpha character of a name, upper-cased, for the card's
 *  placeholder monogram. Falls back to "•". */
export function monogramFor(name: string | null | undefined): string {
  const m = (name ?? "").match(/[A-Za-z0-9]/);
  return m ? m[0].toUpperCase() : "•";
}

// Note: the portfolio card's satellite-still fallback is built with the
// shared `buildSatelliteStaticUrl` from `location-imagery.ts` (it adds
// an address geocode fallback + a building marker), so the earlier
// ad-hoc `buildAerialImageUrl` helper that lived here was removed.
